// src/components/PlantManagement.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plant, plantsAPI, botanicalAPI, BotanicalCharacteristic } from '../services/api';
import { Link } from 'react-router-dom';

const PlantManagement: React.FC = () => {
  const { user } = useAuth();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [characteristics, setCharacteristics] = useState<BotanicalCharacteristic[]>([]);
  const [plantSynonyms, setPlantSynonyms] = useState<{[key: number]: any[]}>({});
  const [plantCommonNames, setPlantCommonNames] = useState<{[key: number]: any[]}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPlant, setEditingPlant] = useState<Plant | null>(null);

  // Debug form state changes
  useEffect(() => {
    console.log('showForm state changed to:', showForm);
  }, [showForm]);

  useEffect(() => {
    console.log('editingPlant state changed to:', editingPlant);
  }, [editingPlant]);

  // Role-based access control
  const canAddPlants = user?.role === 'admin' || user?.role === 'super-admin';
  const canEditPlants = user?.role === 'admin' || user?.role === 'super-admin';
  const canDeletePlants = user?.role === 'super-admin';
  const isSuperAdmin = user?.role === 'super-admin';

  const [formData, setFormData] = useState({
    // Botanical Information
    primary_scientific_name: '',
    family: '',
    synonyms: '',
    common_names: '',
    
    // Basic Information
    genus: '',
    species: '',
    description: '',
    detailed_description: '',
    size_category: '',
    iucn_status: '',
    
    // Classifications and Characteristics
    plant_division: '',
    plant_growth_form: '',
    mode_of_nutrition: '',
    maximum_height_min: '',
    maximum_height_max: '',
    maximum_height_unit: '',
    
    // Biogeography
    native_distribution: '',
    native_habitat: '',
    preferred_climate_zone: '',
    local_conservation_status: '',
    
    // Description and Ethnobotany
    growth_form_description: '',
    foliage_description: '',
    flowers_description: '',
    fruit_description: '',
    
    // Landscaping Features
    desirable_plant_features: '',
    landscape_uses: '',
    
    // Plant Care and Propagation
    light_preference: '',
    water_preference: '',
    plant_growth_rate: '',
    rootzone_tolerance: '',
    propagation_method: '',
    
    // Foliar
    mature_foliage_colours: '',
    mature_foliage_textures: '',
    foliar_type: '',
    foliar_attachment_to_stem: '',
    foliar_shapes: '',
    foliar_venation: '',
    foliar_apex_tip: '',
    foliar_base: '',
    
    // Floral (Angiosperm)
    flower_plant_sexuality: '',
    flower_colours: '',
    flower_grouping: '',
    flower_symmetry: '',
    individual_flower_shape: '',
    flowering_habit: '',
    
    // Fruit, Seed and Spore
    mature_fruit_colours: '',
    
    // Others
    flora_disclaimer: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plantsResponse, characteristicsResponse] = await Promise.all([
        plantsAPI.getPlants(),
        botanicalAPI.getCharacteristics()
      ]);
      
      setPlants(plantsResponse.plants || []);
      setCharacteristics(characteristicsResponse.characteristics || []);
      
      // Load synonyms and common names for each plant
      const synonymsData: {[key: number]: any[]} = {};
      const commonNamesData: {[key: number]: any[]} = {};
      
      for (const plant of plantsResponse.plants || []) {
        try {
          const [synonymsResponse, commonNamesResponse] = await Promise.all([
            plantsAPI.getPlantSynonyms(plant.id),
            plantsAPI.getPlantCommonNames(plant.id)
          ]);
          synonymsData[plant.id] = synonymsResponse.synonyms || [];
          commonNamesData[plant.id] = commonNamesResponse.commonNames || [];
        } catch (err) {
          console.warn(`Failed to load names for plant ${plant.id}:`, err);
          synonymsData[plant.id] = [];
          commonNamesData[plant.id] = [];
        }
      }
      
      setPlantSynonyms(synonymsData);
      setPlantCommonNames(commonNamesData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Client-side validation
      if (!formData.primary_scientific_name || formData.primary_scientific_name.trim() === '') {
        setError('Please enter a scientific name before submitting.');
        return;
      }

      // Validate scientific name format (only letters and spaces)
      const scientificNameRegex = /^[A-Za-z\s]+$/;
      if (!scientificNameRegex.test(formData.primary_scientific_name)) {
        setError('Scientific name should only contain letters and spaces (no numbers or special characters).');
        return;
      }

      // Convert form data to proper types and filter out non-existent database fields
      const plantData: Partial<Plant> = {
        primary_scientific_name: formData.primary_scientific_name,
        family: formData.family,
        genus: formData.genus,
        species: formData.species,
        description: formData.description,
        detailed_description: formData.detailed_description,
        size_category: formData.size_category,
        iucn_status: formData.iucn_status,
        // Convert numeric fields
        height_min: formData.maximum_height_min ? parseFloat(formData.maximum_height_min) : null,
        height_max: formData.maximum_height_max ? parseFloat(formData.maximum_height_max) : null,
      };

      // Convert empty strings to null for optional fields
      Object.keys(plantData).forEach(key => {
        const value = plantData[key as keyof Plant];
        if (value === '' || value === undefined) {
          (plantData as any)[key] = null;
        }
      });

      console.log('Sending plant data:', plantData);

      if (editingPlant) {
        // Update existing plant
        await plantsAPI.updatePlant(editingPlant.id, plantData);
        setSuccess('Plant updated successfully!');
      } else {
        // Create new plant
        await plantsAPI.createPlant(plantData);
        setSuccess('Plant created successfully!');
      }
      
      await loadData();
      setShowForm(false);
      setEditingPlant(null);
      setFormData({
        primary_scientific_name: '', family: '', synonyms: '', common_names: '',
        genus: '', species: '', description: '', detailed_description: '', size_category: '', iucn_status: '',
        plant_division: '', plant_growth_form: '', mode_of_nutrition: '', maximum_height_min: '', maximum_height_max: '', maximum_height_unit: '',
        native_distribution: '', native_habitat: '', preferred_climate_zone: '', local_conservation_status: '',
        growth_form_description: '', foliage_description: '', flowers_description: '', fruit_description: '',
        desirable_plant_features: '', landscape_uses: '',
        light_preference: '', water_preference: '', plant_growth_rate: '', rootzone_tolerance: '', propagation_method: '',
        mature_foliage_colours: '', mature_foliage_textures: '', foliar_type: '', foliar_attachment_to_stem: '', foliar_shapes: '', foliar_venation: '', foliar_apex_tip: '', foliar_base: '',
        flower_plant_sexuality: '', flower_colours: '', flower_grouping: '', flower_symmetry: '', individual_flower_shape: '', flowering_habit: '',
        mature_fruit_colours: '',
        flora_disclaimer: ''
      });
    } catch (err: any) {
      let errorMessage = 'Failed to save plant';
      
      if (err.response?.data?.error) {
        const error = err.response.data.error;
        
        // Handle Zod validation errors
        if (typeof error === 'object' && error.name === 'ZodError') {
          const zodErrors = error.message || error.issues || [];
          const fieldErrors = Array.isArray(zodErrors) ? zodErrors.map((issue: any) => {
            const field = issue.path?.[0] || 'field';
            const message = issue.message || 'Invalid value';
            return `${field.charAt(0).toUpperCase() + field.slice(1)}: ${message}`;
          }).join(', ') : 'Validation error';
          errorMessage = `Please fix the following: ${fieldErrors}`;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else {
          console.log('Full error object:', error);
          errorMessage = JSON.stringify(error);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plant: Plant) => {
    console.log('Edit button clicked for plant:', plant);
    console.log('Plant data available:', Object.keys(plant));
    console.log('Plant values:', Object.values(plant));
    console.log('Setting editingPlant to:', plant);
    setEditingPlant(plant);
    setFormData({
      primary_scientific_name: plant.primary_scientific_name || '',
      family: plant.family || '',
      synonyms: '', // Will be populated from API
      common_names: '', // Will be populated from API
      genus: plant.genus || '',
      species: plant.species || '',
      description: plant.description || '',
      detailed_description: plant.detailed_description || '',
      size_category: plant.size_category || '',
      iucn_status: plant.iucn_status || '',
      // Set all other fields to empty strings since they don't exist in the database
      plant_division: '',
      plant_growth_form: '',
      mode_of_nutrition: '',
      maximum_height_min: '',
      maximum_height_max: '',
      maximum_height_unit: '',
      native_distribution: '',
      native_habitat: '',
      preferred_climate_zone: '',
      local_conservation_status: '',
      growth_form_description: '',
      foliage_description: '',
      flowers_description: '',
      fruit_description: '',
      desirable_plant_features: '',
      landscape_uses: '',
      light_preference: '',
      water_preference: '',
      plant_growth_rate: '',
      rootzone_tolerance: '',
      propagation_method: '',
      mature_foliage_colours: '',
      mature_foliage_textures: '',
      foliar_type: '',
      foliar_attachment_to_stem: '',
      foliar_shapes: '',
      foliar_venation: '',
      foliar_apex_tip: '',
      foliar_base: '',
      flower_plant_sexuality: '',
      flower_colours: '',
      flower_grouping: '',
      flower_symmetry: '',
      individual_flower_shape: '',
      flowering_habit: '',
      mature_fruit_colours: '',
      flora_disclaimer: ''
    });
    console.log('Form data set, showing form...');
    setShowForm(true);
    console.log('showForm state should now be true');
    console.log('Current showForm state:', showForm);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this plant?')) return;
    
    try {
      await plantsAPI.deletePlant(id);
      setSuccess('Plant deleted successfully!');
      await loadData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete plant';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    }
  };

  if (loading && plants.length === 0) {
    return <div>Loading...</div>;
  }

  console.log('Rendering component, showForm:', showForm, 'editingPlant:', editingPlant);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <nav style={{ backgroundColor: '#007bff', padding: '1rem 2rem', marginBottom: '2rem', borderRadius: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Link to="/dashboard" style={{ color: 'white', textDecoration: 'none', marginRight: '2rem', fontWeight: 'bold' }}>
              Palm Garden
            </Link>
            <Link to="/plants" style={{ color: 'white', textDecoration: 'none', marginRight: '1rem', backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.5rem 1rem', borderRadius: '4px' }}>
              Plant Management
            </Link>
          </div>
          <div>
            <span style={{ color: 'white', marginRight: '20px' }}>
              {user?.display_name || user?.username} ({user?.role})
            </span>
          </div>
        </div>
      </nav>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Plant Management</h2>
        {canAddPlants && (
          <button 
            onClick={() => {
              setShowForm(true);
              setEditingPlant(null);
              setFormData({
                primary_scientific_name: '', family: '', synonyms: '', common_names: '',
                genus: '', species: '', description: '', detailed_description: '', size_category: '', iucn_status: '',
                plant_division: '', plant_growth_form: '', mode_of_nutrition: '', maximum_height_min: '', maximum_height_max: '', maximum_height_unit: '',
                native_distribution: '', native_habitat: '', preferred_climate_zone: '', local_conservation_status: '',
                growth_form_description: '', foliage_description: '', flowers_description: '', fruit_description: '',
                desirable_plant_features: '', landscape_uses: '',
                light_preference: '', water_preference: '', plant_growth_rate: '', rootzone_tolerance: '', propagation_method: '',
                mature_foliage_colours: '', mature_foliage_textures: '', foliar_type: '', foliar_attachment_to_stem: '', foliar_shapes: '', foliar_venation: '', foliar_apex_tip: '', foliar_base: '',
                flower_plant_sexuality: '', flower_colours: '', flower_grouping: '', flower_symmetry: '', individual_flower_shape: '', flowering_habit: '',
                mature_fruit_colours: '',
                flora_disclaimer: ''
              });
            }}
            style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
          >
            Add New Plant
          </button>
        )}
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ color: '#155724', marginBottom: '20px', padding: '10px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
          {success}
        </div>
      )}

      {showForm && (
        <div style={{ 
          marginBottom: '30px', 
          padding: '20px', 
          border: '2px solid #007bff', 
          borderRadius: '8px', 
          backgroundColor: '#f8f9fa', 
          maxHeight: 'none', 
          overflow: 'visible',
          position: 'relative',
          zIndex: 1000
        }}>
          <h3 style={{ color: '#007bff', fontSize: '20px', fontWeight: 'bold' }}>
            {editingPlant ? `✏️ Edit Plant: ${editingPlant.primary_scientific_name}` : '➕ Add New Plant'}
          </h3>
          <form onSubmit={handleSubmit} style={{ maxHeight: 'none', overflow: 'visible' }}>
            
            {/* Botanical Information */}
            <div style={{ marginBottom: '30px', border: '2px solid #28a745', padding: '15px', borderRadius: '8px', backgroundColor: '#f8fff8' }}>
              <h4 style={{ color: '#28a745', borderBottom: '2px solid #28a745', paddingBottom: '5px', display: 'flex', alignItems: 'center' }}>
                🌿 Botanical Information
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ color: '#dc3545', fontWeight: 'bold' }}>Scientific Name * (Required):</label>
                  <input 
                    type="text" 
                    name="primary_scientific_name" 
                    value={formData.primary_scientific_name} 
                    onChange={handleInputChange} 
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      fontStyle: 'italic',
                      border: formData.primary_scientific_name && /^[A-Za-z\s]+$/.test(formData.primary_scientific_name) ? '1px solid #28a745' : '2px solid #dc3545',
                      backgroundColor: formData.primary_scientific_name ? '#f8fff8' : '#fff5f5'
                    }} 
                    placeholder="e.g., Melastoma malabathricum" 
                    required
                  />
                  {!formData.primary_scientific_name && <small style={{ color: '#dc3545', fontSize: '12px' }}>⚠️ Scientific name is required</small>}
                  {formData.primary_scientific_name && !/^[A-Za-z\s]+$/.test(formData.primary_scientific_name) && <small style={{ color: '#dc3545', fontSize: '12px' }}>⚠️ Only letters and spaces allowed</small>}
                </div>
                <div>
                  <label>Family:</label>
                  <input type="text" name="family" value={formData.family} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Melastomataceae" />
                </div>
                <div>
                  <label>Synonyms:</label>
                  <textarea name="synonyms" value={formData.synonyms || ''} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '60px', fontStyle: 'italic' }} placeholder="e.g., Melastoma affine, Melastoma candidum" />
                </div>
                <div>
                  <label>Common Names:</label>
                  <textarea name="common_names" value={formData.common_names || ''} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '60px' }} placeholder="e.g., Straits Rhododendron, Singapore Rhododendron" />
                </div>
                <div>
                  <label>Genus:</label>
                  <input type="text" name="genus" value={formData.genus} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Melastoma" />
                </div>
                <div>
                  <label>Species:</label>
                  <input type="text" name="species" value={formData.species} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., malabathricum" />
                </div>
                <div>
                  <label>Description:</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '80px' }} placeholder="Brief description of the plant" />
                </div>
                <div>
                  <label>Detailed Description:</label>
                  <textarea name="detailed_description" value={formData.detailed_description} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '100px' }} placeholder="Comprehensive description of the plant" />
                </div>
                <div>
                  <label>Size Category:</label>
                  <select name="size_category" value={formData.size_category} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Size Category</option>
                    <option value="Big (30m)">Big (30m)</option>
                    <option value="Medium (16m-30m)">Medium (16m-30m)</option>
                    <option value="Small (6m-15m)">Small (6m-15m)</option>
                    <option value="Shrubby (1m-5m)">Shrubby (1m-5m)</option>
                  </select>
                </div>
                <div>
                  <label>IUCN Status:</label>
                  <select name="iucn_status" value={formData.iucn_status} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select IUCN Status</option>
                    <option value="Not Evaluated">Not Evaluated</option>
                    <option value="Data Deficient">Data Deficient</option>
                    <option value="Least Concern">Least Concern</option>
                    <option value="Near Threatened">Near Threatened</option>
                    <option value="Vulnerable">Vulnerable</option>
                    <option value="Endangered">Endangered</option>
                    <option value="Critically Endangered">Critically Endangered</option>
                    <option value="Extinct in the Wild">Extinct in the Wild</option>
                    <option value="Extinct">Extinct</option>
                  </select>
                </div>
              </div>
            </div>


            {/* Classifications and Characteristics */}
            <div style={{ marginBottom: '30px', border: '2px solid #28a745', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>📊 Classifications and Characteristics</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Plant Division:</label>
                  <select name="plant_division" value={formData.plant_division} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Division</option>
                    <option value="Angiosperms (Flowering Seed Plants)">Angiosperms (Flowering Seed Plants)</option>
                    <option value="Gymnosperms (Non-flowering Seed Plants)">Gymnosperms (Non-flowering Seed Plants)</option>
                    <option value="Pteridophytes (Ferns)">Pteridophytes (Ferns)</option>
                    <option value="Bryophytes (Mosses)">Bryophytes (Mosses)</option>
                  </select>
                </div>
                <div>
                  <label>Plant Growth Form:</label>
                  <input type="text" name="plant_growth_form" value={formData.plant_growth_form} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Tree (Shrubby (1m-5m)), Shrub" />
                </div>
                <div>
                  <label>Mode of Nutrition:</label>
                  <select name="mode_of_nutrition" value={formData.mode_of_nutrition} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Mode</option>
                    <option value="Autotrophic">Autotrophic</option>
                    <option value="Heterotrophic">Heterotrophic</option>
                    <option value="Parasitic">Parasitic</option>
                    <option value="Saprophytic">Saprophytic</option>
                  </select>
                </div>
                <div>
                  <label>Maximum Height (Min):</label>
                  <input type="number" name="maximum_height_min" value={formData.maximum_height_min} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., 1.5" />
                </div>
                <div>
                  <label>Maximum Height (Max):</label>
                  <input type="number" name="maximum_height_max" value={formData.maximum_height_max} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., 6" />
                </div>
                <div>
                  <label>Height Unit:</label>
                  <select name="maximum_height_unit" value={formData.maximum_height_unit} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Unit</option>
                    <option value="m">meters</option>
                    <option value="cm">centimeters</option>
                    <option value="ft">feet</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Biogeography */}
            <div style={{ marginBottom: '30px', border: '2px solid #ffc107', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>🌍 Biogeography</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Native Distribution:</label>
                  <textarea name="native_distribution" value={formData.native_distribution} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '60px' }} placeholder="e.g., China, Cambodia, Laos, Myanmar, Thailand, Vietnam" />
                </div>
                <div>
                  <label>Native Habitat:</label>
                  <select name="native_habitat" value={formData.native_habitat} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Habitat</option>
                    <option value="Terrestrial">Terrestrial</option>
                    <option value="Aquatic">Aquatic</option>
                    <option value="Epiphytic">Epiphytic</option>
                    <option value="Lithophytic">Lithophytic</option>
                  </select>
                </div>
                <div>
                  <label>Preferred Climate Zone:</label>
                  <select name="preferred_climate_zone" value={formData.preferred_climate_zone} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Climate Zone</option>
                    <option value="Tropical">Tropical</option>
                    <option value="Subtropical">Subtropical</option>
                    <option value="Temperate">Temperate</option>
                    <option value="Mediterranean">Mediterranean</option>
                    <option value="Desert">Desert</option>
                    <option value="Arctic">Arctic</option>
                  </select>
                </div>
                <div>
                  <label>Local Conservation Status:</label>
                  <input type="text" name="local_conservation_status" value={formData.local_conservation_status} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Non-native (Horticultural / Cultivated Only)" />
                </div>
              </div>
            </div>

            {/* Description and Ethnobotany */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Description and Ethnobotany</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Growth Form Description:</label>
                  <textarea name="growth_form_description" value={formData.growth_form_description} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '80px' }} />
                </div>
                <div>
                  <label>Foliage Description:</label>
                  <textarea name="foliage_description" value={formData.foliage_description} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '80px' }} />
                </div>
                <div>
                  <label>Flowers Description:</label>
                  <textarea name="flowers_description" value={formData.flowers_description} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '80px' }} />
                </div>
                <div>
                  <label>Fruit Description:</label>
                  <textarea name="fruit_description" value={formData.fruit_description} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '80px' }} />
                </div>
              </div>
            </div>

            {/* Landscaping Features */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Landscaping Features</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Desirable Plant Features:</label>
                  <input type="text" name="desirable_plant_features" value={formData.desirable_plant_features} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Ornamental Flowers" />
                </div>
                <div>
                  <label>Landscape Uses:</label>
                  <input type="text" name="landscape_uses" value={formData.landscape_uses} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Parks & Gardens" />
                </div>
              </div>
            </div>

            {/* Plant Care and Propagation */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Plant Care and Propagation</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Light Preference:</label>
                  <select name="light_preference" value={formData.light_preference} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Light Preference</option>
                    <option value="Full Sun">Full Sun</option>
                    <option value="Partial Sun">Partial Sun</option>
                    <option value="Partial Shade">Partial Shade</option>
                    <option value="Full Shade">Full Shade</option>
                  </select>
                </div>
                <div>
                  <label>Water Preference:</label>
                  <select name="water_preference" value={formData.water_preference} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Water Preference</option>
                    <option value="High Water">High Water</option>
                    <option value="Moderate Water">Moderate Water</option>
                    <option value="Low Water">Low Water</option>
                    <option value="Drought Tolerant">Drought Tolerant</option>
                  </select>
                </div>
                <div>
                  <label>Plant Growth Rate:</label>
                  <select name="plant_growth_rate" value={formData.plant_growth_rate} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Growth Rate</option>
                    <option value="Fast">Fast</option>
                    <option value="Fast to Moderate">Fast to Moderate</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Slow">Slow</option>
                  </select>
                </div>
                <div>
                  <label>Rootzone Tolerance:</label>
                  <input type="text" name="rootzone_tolerance" value={formData.rootzone_tolerance} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Moist Soils, Well-Drained Soils" />
                </div>
                <div>
                  <label>Propagation Method:</label>
                  <select name="propagation_method" value={formData.propagation_method} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Propagation Method</option>
                    <option value="Seed">Seed</option>
                    <option value="Cuttings">Cuttings</option>
                    <option value="Division">Division</option>
                    <option value="Grafting">Grafting</option>
                    <option value="Layering">Layering</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Foliar */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Foliar</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Mature Foliage Colours:</label>
                  <input type="text" name="mature_foliage_colours" value={formData.mature_foliage_colours} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Green" />
                </div>
                <div>
                  <label>Mature Foliage Textures:</label>
                  <input type="text" name="mature_foliage_textures" value={formData.mature_foliage_textures} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Papery" />
                </div>
                <div>
                  <label>Foliar Type:</label>
                  <select name="foliar_type" value={formData.foliar_type} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Foliar Type</option>
                    <option value="Simple / Unifoliate">Simple / Unifoliate</option>
                    <option value="Compound">Compound</option>
                    <option value="Palmate">Palmate</option>
                    <option value="Pinnate">Pinnate</option>
                  </select>
                </div>
                <div>
                  <label>Foliar Attachment to Stem:</label>
                  <select name="foliar_attachment_to_stem" value={formData.foliar_attachment_to_stem} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Attachment</option>
                    <option value="Petiolate">Petiolate</option>
                    <option value="Sessile">Sessile</option>
                    <option value="Subsessile">Subsessile</option>
                  </select>
                </div>
                <div>
                  <label>Foliar Shapes:</label>
                  <input type="text" name="foliar_shapes" value={formData.foliar_shapes} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Non-Palm Foliage (Obovate, Elliptical, Oblong, Oblanceolate)" />
                </div>
                <div>
                  <label>Foliar Venation:</label>
                  <select name="foliar_venation" value={formData.foliar_venation} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Venation</option>
                    <option value="Pinnate / Net">Pinnate / Net</option>
                    <option value="Parallel">Parallel</option>
                    <option value="Palmate">Palmate</option>
                    <option value="Dichotomous">Dichotomous</option>
                  </select>
                </div>
                <div>
                  <label>Foliar Apex - Tip:</label>
                  <select name="foliar_apex_tip" value={formData.foliar_apex_tip} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Apex</option>
                    <option value="Acuminate">Acuminate</option>
                    <option value="Acute">Acute</option>
                    <option value="Obtuse">Obtuse</option>
                    <option value="Rounded">Rounded</option>
                    <option value="Emarginate">Emarginate</option>
                  </select>
                </div>
                <div>
                  <label>Foliar Base:</label>
                  <input type="text" name="foliar_base" value={formData.foliar_base} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Cuneate, Rounded / Obtuse" />
                </div>
              </div>
            </div>

            {/* Floral (Angiosperm) */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Floral (Angiosperm)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Flower & Plant Sexuality:</label>
                  <select name="flower_plant_sexuality" value={formData.flower_plant_sexuality} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Sexuality</option>
                    <option value="Bisexual Flowers">Bisexual Flowers</option>
                    <option value="Unisexual Flowers">Unisexual Flowers</option>
                    <option value="Monoecious">Monoecious</option>
                    <option value="Dioecious">Dioecious</option>
                  </select>
                </div>
                <div>
                  <label>Flower Colours:</label>
                  <input type="text" name="flower_colours" value={formData.flower_colours} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., White" />
                </div>
                <div>
                  <label>Flower Grouping:</label>
                  <select name="flower_grouping" value={formData.flower_grouping} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Grouping</option>
                    <option value="Solitary">Solitary</option>
                    <option value="Cluster / Inflorescence">Cluster / Inflorescence</option>
                    <option value="Raceme">Raceme</option>
                    <option value="Panicle">Panicle</option>
                    <option value="Umbel">Umbel</option>
                  </select>
                </div>
                <div>
                  <label>Flower Symmetry:</label>
                  <select name="flower_symmetry" value={formData.flower_symmetry} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Symmetry</option>
                    <option value="Radial">Radial</option>
                    <option value="Bilateral">Bilateral</option>
                    <option value="Asymmetric">Asymmetric</option>
                  </select>
                </div>
                <div>
                  <label>Individual Flower Shape:</label>
                  <input type="text" name="individual_flower_shape" value={formData.individual_flower_shape} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Stellate / Star-shaped" />
                </div>
                <div>
                  <label>Flowering Habit:</label>
                  <select name="flowering_habit" value={formData.flowering_habit} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select Habit</option>
                    <option value="Polycarpic">Polycarpic</option>
                    <option value="Monocarpic">Monocarpic</option>
                    <option value="Semelparous">Semelparous</option>
                    <option value="Iteroparous">Iteroparous</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Fruit, Seed and Spore */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Fruit, Seed and Spore</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Mature Fruit Colours:</label>
                  <input type="text" name="mature_fruit_colours" value={formData.mature_fruit_colours} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} placeholder="e.g., Black" />
                </div>
              </div>
            </div>

            {/* Others */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Others</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label>Flora Disclaimer:</label>
                  <textarea name="flora_disclaimer" value={formData.flora_disclaimer} onChange={handleInputChange} style={{ width: '100%', padding: '8px', height: '100px' }} placeholder="Medical disclaimer text..." />
                </div>
                <div style={{ padding: '15px', backgroundColor: '#e8f4fd', border: '1px solid #007bff', borderRadius: '4px' }}>
                  <h5 style={{ color: '#007bff', margin: '0 0 10px 0' }}>🔢 Auto-Generated IDs</h5>
                  <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                    <strong>Master ID</strong> and <strong>Species ID</strong> will be automatically generated when you save the plant.<br/>
                    • Master ID: M000001, M000002, etc.<br/>
                    • Species ID: SP[Genus3][Species3]-000001 (e.g., SPPhodat-000001 for Phoenix dactylifera)
                  </p>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" disabled={loading} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px' }}>
                {loading ? 'Saving...' : (editingPlant ? 'Update Plant' : 'Add Plant')}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div>
        <h3>Plants ({plants.length})</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          {plants.map((plant) => {
            // Get real synonyms and common names from database
            const synonyms = plantSynonyms[plant.id] || [];
            const commonNames = plantCommonNames[plant.id] || [];

            return (
            <div key={plant.id} style={{ padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#fafafa', marginBottom: '20px' }}>
              
              {/* Botanical Information Section */}
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '20px', 
                borderRadius: '6px', 
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <h3 style={{ 
                  color: '#2c5530', 
                  marginBottom: '15px', 
                  fontSize: '18px',
                  fontWeight: '600',
                  borderBottom: '2px solid #28a745',
                  paddingBottom: '8px'
                }}>
                  🌿 Botanical Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>Scientific Name</span>
                      <div style={{ 
                        fontSize: '16px', 
                        fontStyle: 'italic', 
                        color: '#2c5530',
                        fontWeight: '500',
                        marginTop: '4px'
                      }}>
                        {plant.primary_scientific_name || `${plant.genus || 'Unknown'} ${plant.species || 'species'}`}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>Family</span>
                      <div style={{ 
                        fontSize: '15px', 
                        color: '#495057',
                        marginTop: '4px'
                      }}>
                        {plant.family || 'Not specified'}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>Synonyms</span>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#6c757d',
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}>
                        {synonyms.length > 0 ? synonyms.map(s => s.synonym_name).join(', ') : 'No synonyms available'}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>Common Names</span>
                      <div style={{ 
                        fontSize: '15px', 
                        color: '#495057',
                        marginTop: '4px'
                      }}>
                        {commonNames.length > 0 ? commonNames.map(c => c.common_name).join(', ') : 'No common names available'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Plant Details */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: '#2c5530', marginBottom: '15px' }}>{plant.primary_scientific_name}</h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                    <div><strong>Status:</strong> <span style={{ color: plant.status === 'published' ? '#28a745' : '#ffc107' }}>{plant.status}</span></div>
                    <div><strong>UUID:</strong> {plant.species_uuid}</div>
                    {plant.family && <div><strong>Family:</strong> {plant.family}</div>}
                    {plant.genus && <div><strong>Genus:</strong> {plant.genus}</div>}
                    {plant.species && <div><strong>Species:</strong> {plant.species}</div>}
                    {plant.size_category && <div><strong>Size Category:</strong> {plant.size_category}</div>}
                    {plant.iucn_status && <div><strong>IUCN Status:</strong> {plant.iucn_status}</div>}
                    {plant.description && <div><strong>Description:</strong> {plant.description}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', zIndex: 1000, position: 'relative' }}>
                  {canEditPlants && (
                    <button
                      onClick={() => handleEdit(plant)}
                      style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer',
                        zIndex: 999,
                        position: 'relative',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {canDeletePlants && (
                    <button
                      onClick={() => handleDelete(plant.id)}
                      style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlantManagement;